import { useEffect } from 'react';
import { ArrowLeft, MapPin, Music, Trees, Utensils, Users, Calendar, Sun, Leaf, Snowflake, Flower } from 'lucide-react';

interface GuidePageProps {
  onBack: () => void;
}

const neighborhoods = [
  {
    name: 'Cultural District & Downtown',
    desc: "Pittsburgh's Cultural District is the beating heart of the city's arts and entertainment scene. Located in the Golden Triangle, this compact neighborhood hosts the Benedum Center, Heinz Hall, and dozens of smaller theaters and galleries. Year-round you'll find Broadway touring productions, the Pittsburgh Symphony Orchestra, Pittsburgh Ballet Theatre, and the Pittsburgh Opera. The streets come alive for First Friday gallery crawls every month, and outdoor plazas host free concerts during summer. Downtown Pittsburgh also connects to PNC Park and Acrisure Stadium via the Roberto Clemente Bridge, making it easy to pair a Pirates or Steelers game with dinner on Penn Avenue.",
  },
  {
    name: 'Strip District',
    desc: "The Strip District has evolved from a historic produce and food distribution hub into one of Pittsburgh's most vibrant entertainment corridors. On weekend mornings the Strip bursts with farmers markets, specialty food vendors, and street performers along Penn Avenue. The neighborhood regularly hosts food festivals, beer gardens, and pop-up art shows. Smallman Street is lined with bars and music venues that keep the area lively into the evening. The Strip's proximity to the riverfront also makes it a popular anchor for Pittsburgh's outdoor event season, with riverfront festivals and fireworks visible from its edges.",
  },
  {
    name: 'Lawrenceville',
    desc: "Lawrenceville has become Pittsburgh's trendiest neighborhood for independent arts, food, and music events. Butler Street is packed with independent shops, craft breweries, and galleries that participate in regular First Friday art walks. The neighborhood's warehouse spaces and music venues host everything from indie rock shows to burlesque performances and comedy nights. Lawrenceville is also home to multiple craft breweries that regularly feature live music and community events on their taproom calendars. If you're looking for something unexpected and local, Lawrenceville is usually the place.",
  },
  {
    name: 'South Side',
    desc: "East Carson Street on Pittsburgh's South Side is one of the longest stretches of bars and restaurants in the country — and the events calendar to match. The South Side hosts bar crawls, charity runs, outdoor music nights, and seasonal festivals throughout the year. SouthSide Works is an outdoor shopping and entertainment complex that runs a packed calendar of free outdoor concerts, holiday markets, and community screenings throughout the warmer months. The neighborhood sits directly on the riverfront with trails connecting to the Great Allegheny Passage, making it ideal for combining outdoor recreation with evening events.",
  },
  {
    name: 'North Shore & PNC Park',
    desc: "The North Shore is Pittsburgh's sports and stadium district, home to PNC Park (Pittsburgh Pirates) and Acrisure Stadium (Pittsburgh Steelers). Beyond game days, the North Shore hosts pre-game festivals, outdoor screenings, and a summer-long events calendar on the riverfront plaza. The Great Allegheny Passage trailhead is here, and the Three Rivers Heritage Trail provides a scenic walk or bike ride connecting North Shore to the Strip District and beyond. The view of downtown Pittsburgh from North Shore is one of the most photographed in the city.",
  },
  {
    name: 'Oakland',
    desc: "Home to the University of Pittsburgh and Carnegie Mellon University, Oakland is Pittsburgh's academic and cultural hub. The Carnegie Museums of Art and Natural History host regular evening events, lectures, and special exhibitions. Schenley Park — one of Pittsburgh's largest urban parks — is the site of summer concerts, outdoor fitness classes, and the Oval skating rink in winter. Phipps Conservatory runs a world-class seasonal flower show calendar, and the Pittsburgh Botanic Garden hosts outdoor events year-round. Oakland is where you'll find many of Pittsburgh's free and low-cost cultural events.",
  },
];

const seasons = [
  {
    Icon: Flower,
    name: 'Spring',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    events: [
      'Pittsburgh St. Patrick\'s Day Parade (March)',
      'Three Rivers Arts Festival preview events (May)',
      'Pittsburgh Marathon (May)',
      'Phipps Spring Flower Show',
      'Opening Day at PNC Park',
      'Neighborhood garden tours and home-and-garden shows',
    ],
  },
  {
    Icon: Sun,
    name: 'Summer',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    events: [
      'Three Rivers Arts Festival (June)',
      'Pittsburgh Pride (June)',
      'Pittsburgh Vintage Grand Prix (July)',
      'Pittsburgh Regatta (July)',
      'Picklesburgh (July–August)',
      'Pittsburgh Folk Festival',
      'Outdoor concerts in Schenley Park & Hartwood Acres',
      'Pittsburgh Steelers & Pirates home games',
    ],
  },
  {
    Icon: Leaf,
    name: 'Fall',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    events: [
      'Pittsburgh Film Festival (October)',
      'South Side Halloween Parade',
      'Pittsburgh Oktoberfest celebrations',
      'Harvest markets and pumpkin festivals in surrounding boroughs',
      'Pittsburgh Penguins season opener',
      'Carnegie Museums Gala and fundraiser events',
    ],
  },
  {
    Icon: Snowflake,
    name: 'Winter',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    events: [
      'PPG Place Ice Rink (December–February)',
      'Light Up Night Downtown Pittsburgh (November)',
      'Holiday markets in the Strip District',
      'First Night Pittsburgh (New Year\'s Eve)',
      'Pittsburgh Symphony winter concerts',
      'Pittsburgh Penguins home games throughout the season',
    ],
  },
];

const categories = [
  {
    Icon: Music,
    name: 'Live Music & Concerts',
    desc: 'Pittsburgh has a thriving live music scene spanning every genre. Major venues include the Benedum Center, Stage AE, UPMC Events Center, and Petersen Events Center for large acts. Mid-size venues like Mr. Smalls, Roxian Theatre, and Club Café host national touring acts in an intimate setting. Dozens of bars throughout Lawrenceville, South Side, and East Liberty feature local and regional acts on weekends.',
  },
  {
    Icon: Utensils,
    name: 'Food & Drink',
    desc: 'Pittsburgh\'s food and drink event scene is booming. The Strip District hosts weekend food markets year-round. Pittsburgh Restaurant Week (twice yearly) offers prix-fixe menus at hundreds of restaurants. Craft breweries like East End Brewing, Full Pint, and Dancing Gnome run regular tap takeovers and pairing dinners. Pittsburgh also hosts specialized food festivals dedicated to pierogies, BBQ, pizza, and more throughout the year.',
  },
  {
    Icon: Trees,
    name: 'Outdoor & Recreation',
    desc: 'Pittsburgh\'s parks and river trails are the backdrop for a wide range of outdoor events. The Three Rivers Heritage Trail connects 24 miles of riverfront paths and is the site of charity runs, cycling events, and outdoor yoga sessions. Frick Park, Schenley Park, and North Park host hiking challenges, disc golf tournaments, and fitness festivals. The rivers themselves host Dragon Boat racing, the Pittsburgh Regatta, and kayaking tours that run from spring through fall.',
  },
  {
    Icon: Users,
    name: 'Arts, Theatre & Culture',
    desc: 'The Pittsburgh Cultural District anchors a dense calendar of theatre, dance, and visual arts events year-round. Beyond the major venues, the city\'s neighborhood galleries, independent theatres, and pop-up art spaces add hundreds of events to the calendar each month. Carnegie Museum of Art, the Andy Warhol Museum, and the Children\'s Museum of Pittsburgh run rotating exhibitions with accompanying public programs, artist talks, and late-night events.',
  },
  {
    Icon: Calendar,
    name: 'Festivals & Fairs',
    desc: 'Pittsburgh\'s summer and fall festival season is legendary among mid-size American cities. The Three Rivers Arts Festival draws hundreds of thousands of visitors over two weeks in June. Festivals dedicated to specific neighborhoods — Bloomfield\'s Little Italy Days, Lawrenceville\'s Out in the Street, the South Side\'s SouthSide Summer Street Spectacular — give each community its own annual celebration. County fairs in Allegheny County\'s surrounding boroughs extend the festival season well into fall.',
  },
];

export default function GuidePage({ onBack }: GuidePageProps) {
  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = descEl?.content ?? '';
    document.title = 'Pitt Fest Pittsburgh & Pitt Parties — Events Guide & Calendar | Get Out Pitt';
    descEl?.setAttribute('content', 'The complete guide to Pitt Fest Pittsburgh events, Pitt parties, and things to do in Pittsburgh, PA. Explore neighborhoods, seasonal festivals, live music, food events, and outdoor activities year-round.');
    return () => {
      document.title = prevTitle;
      descEl?.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-zinc-950 text-white z-20 border-b border-zinc-800/80">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Events
          </button>
          <span className="text-[17px] font-bold tracking-tight leading-none">
            Get Out <span className="text-amber-400">Pitt</span>
          </span>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-5 sm:px-8 py-12">

        {/* Hero */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 bg-amber-50 px-3 py-1 rounded-full mb-4">
            <MapPin className="w-3 h-3" />
            Pittsburgh, PA
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">
            Pitt Fest Pittsburgh &amp; Pitt Parties: Things to Do in Pittsburgh
          </h1>
          <p className="text-slate-500 text-lg leading-relaxed max-w-2xl">
            Pittsburgh has more events than you might think. This guide covers the neighborhoods, seasons, and categories that make it a great city for things to do — whether you live here or are just visiting.
          </p>
        </div>

        {/* Intro section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Pittsburgh's Events Scene</h2>
          <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-4">
            <p>
              Pittsburgh knows how to come together. Riverfront festivals draw big crowds each summer. Smaller gallery walks and brewpub concerts fill the calendar the rest of the year. There is something happening here nearly every day.
            </p>
            <p>
              The city's layout helps. Three rivers, many hillside neighborhoods, a dense downtown, and large parks give events room to spread out. One weekend can offer a riverfront marathon, a craft beer fest, an outdoor symphony, and a street fair in Bloomfield.
            </p>
            <p>
              Pittsburgh's arts scene is large for a city its size. The Carnegie Museums, Symphony, Opera, and Ballet keep the cultural calendar full all year — not just in summer.
            </p>
          </div>
        </section>

        {/* Neighborhoods */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Pittsburgh Neighborhoods for Events</h2>
          <p className="text-slate-500 mb-8">
            Pittsburgh's neighborhoods each have a distinct character and event culture. Here's where to look depending on what you're after.
          </p>
          <div className="space-y-6">
            {neighborhoods.map((n) => (
              <div key={n.name} className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 text-lg mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  {n.name}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{n.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Seasonal */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Pittsburgh Events by Season</h2>
          <p className="text-slate-500 mb-8">
            Pittsburgh has four distinct seasons, and the events calendar follows them closely.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            {seasons.map(({ Icon, name, color, bg, events }) => (
              <div key={name} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className={`inline-flex items-center gap-2 text-sm font-bold mb-4 ${color}`}>
                  <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  {name}
                </div>
                <ul className="space-y-1.5">
                  {events.map((e) => (
                    <li key={e} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="text-amber-400 mt-1 flex-shrink-0">–</span>
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Categories */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Events by Category</h2>
          <p className="text-slate-500 mb-8">
            Pittsburgh's event scene covers every interest. Here's a breakdown of the major categories.
          </p>
          <div className="space-y-5">
            {categories.map(({ Icon, name, desc }) => (
              <div key={name} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4.5 h-4.5 text-amber-500" />
                  </div>
                  <h3 className="font-bold text-slate-900">{name}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Tips */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Tips for Finding Events in Pittsburgh</h2>
          <p className="text-slate-500 mb-6">A few things that'll make navigating Pittsburgh's event scene easier.</p>
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {[
              {
                title: 'Check dates closer to the event',
                body: 'Pittsburgh events — particularly outdoor ones — frequently update their start times, location details, and ticket availability in the days leading up to the event. Always confirm details on the official event page before heading out.',
              },
              {
                title: 'Free parking is available in many neighborhoods',
                body: 'South Side, Lawrenceville, and Bloomfield have street parking that\'s often free on weekends. For Downtown and the Cultural District, the Pittsburgh Parking Authority app covers most garages at reasonable flat rates on evenings and weekends.',
              },
              {
                title: 'Pittsburgh is a very walkable city once you\'re parked',
                body: 'Most neighborhoods have their venues concentrated within a few blocks. Park once and walk between bars, restaurants, and venues — especially on East Carson Street, Butler Street, and Penn Avenue.',
              },
              {
                title: 'Weather affects Pittsburgh events significantly',
                body: 'Pittsburgh\'s weather can change quickly, especially in spring and fall. Outdoor events are common from May through October, but always have a backup plan. Many venues have covered outdoor spaces.',
              },
              {
                title: 'The Pittsburgh Cultural Trust email list is worth joining',
                body: 'The Cultural Trust manages most of the major Downtown venues and sends a weekly digest of upcoming events across the Cultural District. It\'s one of the best single sources for theatre, music, and cultural events.',
              },
            ].map(({ title, body }) => (
              <div key={title} className="px-6 py-5">
                <h3 className="font-semibold text-slate-800 text-sm mb-1">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-zinc-950 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-bold text-white mb-2">See What's Happening Now</h2>
          <p className="text-zinc-400 text-sm mb-6">
            Browse the interactive map to find events happening near you in Pittsburgh right now.
          </p>
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-sm rounded-lg transition-colors"
          >
            <MapPin className="w-4 h-4" />
            Open the Event Map
          </button>
        </section>
      </main>

      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        <p>&copy; {new Date().getFullYear()} Get Out Pitt. All rights reserved.</p>
      </footer>
    </div>
  );
}
